import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Select from "../../inputs/select";
import CriterionSummary from "./CriterionSummary";
import { getDomainsByTag } from "../../../services/tag";
import "./DomainsCard.scss";

/**
 * DomainsCard - Criteria card for domain targeting
 *
 * Data format: { domains: ["example.com", "test.com"] }
 */
function DomainsCard({ data, onChange, isReadOnly, isCollapsed }) {
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(true);

	const tagId = localStorage.getItem("activeSilo");

	// Convert data.domains array to react-select format
	const selectedDomains = (data?.domains || []).map((d) => ({
		label: d,
		value: d,
	}));

	// Fetch available domains on mount
	useEffect(() => {
		const fetchDomains = async () => {
			if (!tagId) return;

			try {
				setLoading(true);
				const response = await getDomainsByTag(tagId);
				const domainOptions = response.data.map((d) => ({
					label: d,
					value: d,
				}));
				setOptions(domainOptions);
			} catch (error) {
				console.error("Failed to fetch domains:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchDomains();
	}, [tagId]);

	// Collapsed view - show domain chips
	if (isCollapsed) {
		return (
			<div className="domains-card collapsed">
				<CriterionSummary type="domains" data={data} />
			</div>
		);
	}

	// Handle Select component change
	const handleSelectChange = (selectedOptions) => {
		const domains = (selectedOptions || []).map((item) => item.value);
		onChange({ domains });
	};

	// Expanded view - show full selector
	return (
		<div className="domains-card expanded">
			{isReadOnly ? (
				<div className="domains-readonly">
					{selectedDomains.length === 0 ? (
						<p className="no-domains">No domains selected</p>
					) : (
						<div className="domain-chips">
							{selectedDomains.map((domain) => (
								<span key={domain.value} className="domain-chip">
									{domain.label}
								</span>
							))}
						</div>
					)}
				</div>
			) : (
				<Select
					name="domains"
					label="Domains"
					options={options}
					selected={selectedDomains}
					onSubmit={handleSelectChange}
					disabled={loading}
					required={false}
					multi={true}
					search={true}
					creatable={true}
					placeholder={loading ? "Loading domains..." : "Select domains to target"}
				/>
			)}
		</div>
	);
}

DomainsCard.propTypes = {
	data: PropTypes.shape({
		domains: PropTypes.arrayOf(PropTypes.string),
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	isCollapsed: PropTypes.bool,
};

DomainsCard.defaultProps = {
	data: { domains: [] },
	isReadOnly: false,
	isCollapsed: false,
};

export default DomainsCard;
